import React, { useEffect, createElement } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
interface SEOProps {
  page?: string;
}
type Language = 'en' | 'ru' | 'ua' | 'pl';
interface PageMeta {
  title: string;
  description: string;
}
interface PageSEO {
  en: PageMeta;
  ru: PageMeta;
  ua: PageMeta;
  pl: PageMeta;
}
// SEO data with price placeholder {PRICE}
const getSeoData = (price: string): Record<string, PageSEO> => ({
  home: {
    en: {
      title: `Website Design & Development for ${price} | Jetly Agency`,
      description: `Modern business website for ${price}. Get a free preview in 1–3 days, request edits, and pay only if you approve. Remote team, worldwide.`
    },
    ru: {
      title: `Сайт под ключ за ${price} — бесплатное демо | Jetly Agency`,
      description: `Современный сайт для бизнеса за ${price}. Сначала делаем бесплатное демо (1–3 дня), вносим правки, и вы платите только после согласования.`
    },
    ua: {
      title: `Сайт під ключ за ${price} — безкоштовне демо | Jetly Agency`,
      description: `Сучасний сайт для бізнесу за ${price}. Спочатку робимо безкоштовне демо (1–3 дні), вносимо правки, і ви сплачуєте лише після погодження.`
    },
    pl: {
      title: `Strona WWW za ${price} — darmowy podgląd | Jetly Agency`,
      description: `Nowoczesna strona internetowa za ${price}. Darmowy podgląd w 1–3 dni, poprawki na życzenie i płatność dopiero po akceptacji.`
    }
  },
  referral: {
    en: {
      title: 'Referral Program — Earn €100 per Referral | Jetly Agency',
      description: 'Join Jetly Agency referral program. Earn €100 for every successful referral. No limits on earnings. Fast payouts within 7 days.'
    },
    ru: {
      title: 'Реферальная программа — Зарабатывайте €100 за рекомендацию | Jetly Agency',
      description: 'Присоединяйтесь к реферальной программе Jetly Agency. Получайте €100 за каждого приведённого клиента. Без лимитов. Выплаты в течение 7 дней.'
    },
    ua: {
      title: 'Реферальна програма — Заробляйте €100 за рекомендацію | Jetly Agency',
      description: 'Приєднуйтесь до реферальної програми Jetly Agency. Отримуйте €100 за кожного приведеного клієнта. Без лімітів. Виплати протягом 7 днів.'
    },
    pl: {
      title: 'Program poleceń — Zarabiaj €100 za polecenie | Jetly Agency',
      description: 'Dołącz do programu poleceń Jetly Agency. Zarabiaj €100 za każde skuteczne polecenie. Bez limitów. Szybkie wypłaty w ciągu 7 dni.'
    }
  },
  privacy: {
    en: {
      title: 'Privacy Policy | Jetly Agency',
      description: 'Jetly Agency Privacy Policy. Learn how we collect, use, and protect your personal data. GDPR compliant.'
    },
    ru: {
      title: 'Политика конфиденциальности | Jetly Agency',
      description: 'Политика конфиденциальности Jetly Agency. Узнайте, как мы собираем, используем и защищаем ваши персональные данные.'
    },
    ua: {
      title: 'Політика конфіденційності | Jetly Agency',
      description: 'Політика конфіденційності Jetly Agency. Дізнайтеся, як ми збираємо, використовуємо та захищаємо ваші персональні дані.'
    },
    pl: {
      title: 'Polityka prywatności | Jetly Agency',
      description: 'Polityka prywatności Jetly Agency. Dowiedz się, jak zbieramy, wykorzystujemy i chronimy Twoje dane osobowe.'
    }
  },
  terms: {
    en: {
      title: 'Terms of Service | Jetly Agency',
      description: 'Jetly Agency Terms of Service. Read our terms and conditions for website development services.'
    },
    ru: {
      title: 'Условия использования | Jetly Agency',
      description: 'Условия использования Jetly Agency. Ознакомьтесь с условиями предоставления услуг по разработке сайтов.'
    },
    ua: {
      title: 'Умови використання | Jetly Agency',
      description: 'Умови використання Jetly Agency. Ознайомтеся з умовами надання послуг з розробки сайтів.'
    },
    pl: {
      title: 'Warunki użytkowania | Jetly Agency',
      description: 'Warunki użytkowania Jetly Agency. Przeczytaj nasze warunki świadczenia usług tworzenia stron.'
    }
  },
  thankYou: {
    en: {
      title: 'Thank You! | Jetly Agency',
      description: 'Thank you for your inquiry. We will contact you shortly with your free website demo.'
    },
    ru: {
      title: 'Спасибо! | Jetly Agency',
      description: 'Спасибо за вашу заявку. Мы свяжемся с вами в ближайшее время с бесплатным демо сайта.'
    },
    ua: {
      title: 'Дякуємо! | Jetly Agency',
      description: "Дякуємо за вашу заявку. Ми зв'яжемося з вами найближчим часом з безкоштовним демо сайту."
    },
    pl: {
      title: 'Dziękujemy! | Jetly Agency',
      description: 'Dziękujemy za zapytanie. Skontaktujemy się z Tobą wkrótce z darmowym demo strony.'
    }
  },
  thankYouReferral: {
    en: {
      title: 'Welcome to Referral Program! | Jetly Agency',
      description: 'Thank you for joining our referral program. We will send you your unique referral link shortly.'
    },
    ru: {
      title: 'Добро пожаловать в реферальную программу! | Jetly Agency',
      description: 'Спасибо за присоединение к реферальной программе. Мы отправим вам уникальную реферальную ссылку.'
    },
    ua: {
      title: 'Ласкаво просимо до реферальної програми! | Jetly Agency',
      description: 'Дякуємо за приєднання до реферальної програми. Ми надішлемо вам унікальне реферальне посилання.'
    },
    pl: {
      title: 'Witamy w programie poleceń! | Jetly Agency',
      description: 'Dziękujemy za dołączenie do programu poleceń. Wkrótce wyślemy Ci unikalny link polecający.'
    }
  }
});
// URL paths for each page
const pagePaths: Record<string, string> = {
  home: '',
  referral: 'referral',
  privacy: 'privacy',
  terms: 'terms',
  thankYou: 'thank-you',
  thankYouReferral: 'thank-you-referral'
};
export function SEO({
  page = 'home'
}: SEOProps) {
  const {
    language
  } = useLanguage();
  const {
    price
  } = usePricing();
  useEffect(() => {
    const seoData = getSeoData(price);
    const pageData = seoData[page] || seoData.home;
    const currentMeta = pageData[language as Language] || pageData.en;
    const pagePath = pagePaths[page] || '';
    // Update title
    document.title = currentMeta.title;
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', currentMeta.description);
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const baseUrl = 'https://jetly.agency';
    const langPrefix = language === 'en' ? '' : `/${language}`;
    const canonicalUrl = `${baseUrl}${langPrefix}${pagePath ? `/${pagePath}` : ''}`;
    canonical.setAttribute('href', canonicalUrl);
    // Update hreflang tags
    const languages: Language[] = ['en', 'ru', 'ua', 'pl'];
    // Remove existing hreflang tags
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    // Add hreflang tags for all languages
    languages.forEach(lang => {
      const hreflang = document.createElement('link');
      hreflang.setAttribute('rel', 'alternate');
      hreflang.setAttribute('hreflang', lang === 'ua' ? 'uk' : lang);
      const langPrefixHref = lang === 'en' ? '' : `/${lang}`;
      hreflang.setAttribute('href', `${baseUrl}${langPrefixHref}${pagePath ? `/${pagePath}` : ''}`);
      document.head.appendChild(hreflang);
    });
    // Add x-default hreflang
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', `${baseUrl}${pagePath ? `/${pagePath}` : ''}`);
    document.head.appendChild(xDefault);
    // Update Open Graph tags
    const ogTags = [{
      property: 'og:title',
      content: currentMeta.title
    }, {
      property: 'og:description',
      content: currentMeta.description
    }, {
      property: 'og:url',
      content: canonicalUrl
    }, {
      property: 'og:type',
      content: 'website'
    }, {
      property: 'og:site_name',
      content: 'Jetly Agency'
    }, {
      property: 'og:locale',
      content: language === 'en' ? 'en_US' : language === 'ru' ? 'ru_RU' : language === 'ua' ? 'uk_UA' : 'pl_PL'
    }];
    ogTags.forEach(({
      property,
      content
    }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    // Update Twitter Card tags
    const twitterTags = [{
      name: 'twitter:card',
      content: 'summary_large_image'
    }, {
      name: 'twitter:title',
      content: currentMeta.title
    }, {
      name: 'twitter:description',
      content: currentMeta.description
    }];
    twitterTags.forEach(({
      name,
      content
    }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    // Update html lang attribute
    document.documentElement.lang = language === 'ua' ? 'uk' : language;
  }, [language, page, price]);
  return null;
}